import { motion } from 'framer-motion';
import { Sparkles, ShieldCheck, HeartHandshake, Star } from 'lucide-react';

// Pilares de confiança — substituem depoimentos até coletarmos provas reais do beta.
const trustPillars = [
  {
    icon: Sparkles,
    title: 'Pioneiro no Brasil',
    text: 'O primeiro aplicativo brasileiro pensado exclusivamente para a menopausa, em português e feito para a sua realidade.',
  },
  {
    icon: ShieldCheck,
    title: 'Seus dados, protegidos',
    text: 'Tudo o que você registra é privado e criptografado. Suas informações são suas — nunca vendemos seus dados.',
  },
  {
    icon: HeartHandshake,
    title: 'Sem risco para começar',
    text: '7 dias com acesso completo, sem cartão de crédito. Experimente primeiro, decida depois — sem compromisso.',
  },
];

// Quando você tiver os depoimentos REAIS do beta, preencha este array e a seção
// de depoimentos abaixo aparece automaticamente. Deixe vazio até lá.
const realTestimonials = [
  // {
  //   name: 'Nome Real',
  //   age: '50 anos',
  //   location: 'Cidade, UF',
  //   text: 'Depoimento real coletado com autorização.',
  //   rating: 5,
  // },
];

const staggerContainer = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function TestimonialsSection() {
  const hasTestimonials = realTestimonials.length > 0;

  return (
    <section
      id="depoimentos"
      data-testid="testimonials-section"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #fafaf9 0%, #FFF5F8 50%, #fafaf9 100%)' }}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FAADC7] to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-sm font-semibold text-[#B54182] uppercase tracking-widest mb-4 block">
            Por que confiar no MenoEase
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Feito com{' '}
            <span className="text-[#B54182]">cuidado</span>, para você se sentir segura
          </h2>
          <p className="text-base md:text-lg text-stone-500">
            Um espaço pensado para a mulher na menopausa — com privacidade, acolhimento e zero pressão para decidir.
          </p>
        </motion.div>

        {/* Pilares de confiança */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6 md:gap-8"
        >
          {trustPillars.map((pillar) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={pillar.title}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                className="bg-white rounded-3xl p-8 border border-[#FAADC7]/30 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgba(181,65,130,0.08)] transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#FAADC7]/25 text-[#B54182] flex items-center justify-center mb-6">
                  <Icon size={26} />
                </div>
                <h3
                  className="text-xl font-bold text-stone-900 mb-3"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {pillar.title}
                </h3>
                <p className="text-stone-600 leading-relaxed text-base">
                  {pillar.text}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Depoimentos reais — só aparece quando o array realTestimonials tiver itens */}
        {hasTestimonials && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-2xl mx-auto mt-24 mb-12"
            >
              <h3
                className="text-2xl sm:text-3xl font-bold text-stone-900"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                O que nossas usuárias dizem
              </h3>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-6 md:gap-8"
            >
              {realTestimonials.map((t, i) => (
                <motion.div
                  key={i}
                  variants={staggerItem}
                  whileHover={{ y: -6 }}
                  className="bg-white rounded-3xl p-8 border border-[#FAADC7]/30 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300"
                  data-testid={`testimonial-card-${i}`}
                >
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating || 5 }).map((_, j) => (
                      <Star key={j} size={16} className="text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-stone-600 leading-relaxed mb-6 text-base italic">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div>
                    <div className="font-semibold text-stone-900 text-sm">{t.name}</div>
                    <div className="text-xs text-stone-400">{t.age} — {t.location}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
}
